import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import get from '../../../services/axios/get';
import post from '../../../services/axios/post';
import { customAlert } from '../../../store/actions/alert';
import Loader from '../../UI/Loader/Loader';
import Input from '../../UI/Input/Input';
import Button from '../../UI/Button/Button';
import classes from './Settings.module.scss';
import PriceList from '../Prices/PriceList/PriceList';

interface SettingsData {
  id: string;
  companyName: string;
  vatNumber: string;
  address: string;
  contactNumber: string;
  email: string;
  updatedAt: string;
  updatedBy: string;
}

interface InsuranceDocument {
  id: string;
  ownerId: string;
  documentName: string;
  key: string;
  signedUrl?: string;
  createdAt: string;
  updatedAt: string;
}

const Settings: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.auth.user);
  const isMounted = useRef(true);
  const [isSettingsLoading, setIsSettingsLoading] = useState(false);
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);
  const [settings, setSettings] = useState<SettingsData | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [insuranceDocuments, setInsuranceDocuments] = useState<InsuranceDocument[]>([]);

  const [companyName, setCompanyName] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [address, setAddress] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [email, setEmail] = useState('');

  // Track if data has been loaded
  const [dataFetched, setDataFetched] = useState(false);
  const [activeTab, setActiveTab] = useState('company');

  const getSettings = useCallback(() => {
    if (!isMounted.current) return;

    setIsSettingsLoading(true);
    get(
      '/invoice-defaults',
      {},
      (res) => {
        if (isMounted.current) {
          const data = res.data;
          setSettings(data);

          if (data.companyName) setCompanyName(data.companyName);
          if (data.vatNumber) setVatNumber(data.vatNumber);
          if (data.address) setAddress(data.address);
          if (data.contactNumber) setContactNumber(data.contactNumber);
          if (data.email) setEmail(data.email);

          setIsSettingsLoading(false);
        }
      },
      (err) => {
        if (isMounted.current) {
          if (err !== 'Not Found') {
            dispatch(customAlert(true, err));
          }
          setIsSettingsLoading(false);
        }
      }
    );
  }, [dispatch]);

  const getInsuranceDocuments = useCallback(() => {
    if (!isMounted.current) return;
    if (!user?.id || user?.userType !== 'owner') {
      setIsDocumentsLoading(false);
      return;
    }

    setIsDocumentsLoading(true);
    get(
      '/insurance',
      { ownerId: user.id },
      (res) => {
        if (isMounted.current) {
          setInsuranceDocuments(res.data);
          setIsDocumentsLoading(false);
        }
      },
      (err) => {
        if (isMounted.current) {
          if (err !== 'Not Found') {
            dispatch(customAlert(true, err));
          }
          setIsDocumentsLoading(false);
        }
      }
    );
  }, [dispatch, user?.id, user?.userType]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const uploadGoodsInTransportDocument = () => {
    if (!selectedFile || !user?.id) {
      dispatch(customAlert(true, 'Please select a file to upload'));
      return;
    }

    setIsFileUploading(true);
    const documentName = 'Goods In Transport';

    // Step 1: Get upload URL
    get(
      '/insurance/upload',
      { ownerId: user.id, documentName },
      async (res) => {
        if (isMounted.current) {
          try {
            // Step 2: Upload file to the signed URL
            const { uploadUrl } = res.data;
            await fetch(uploadUrl, {
              method: 'PUT',
              body: selectedFile,
              headers: {
                'Content-Type': selectedFile.type
              }
            });

            // Step 3: Save document reference in database
            post(
              '/insurance',
              {
                ownerId: user.id,
                documentName
              },
              () => {
                if (isMounted.current) {
                  dispatch(customAlert(true, 'Document uploaded successfully'));
                  setSelectedFile(null);
                  getInsuranceDocuments();
                  setIsFileUploading(false);
                }
              },
              (err) => {
                if (isMounted.current) {
                  dispatch(customAlert(true, err));
                  setIsFileUploading(false);
                }
              }
            );
          } catch (error) {
            if (isMounted.current) {
              dispatch(customAlert(true, 'Error uploading document'));
              setIsFileUploading(false);
            }
          }
        }
      },
      (err) => {
        if (isMounted.current) {
          dispatch(customAlert(true, err));
          setIsFileUploading(false);
        }
      }
    );
  };

  const saveSettings = () => {
    if (!companyName.trim() || !vatNumber.trim() || !address.trim()) {
      dispatch(customAlert(true, 'All fields are required'));
      return;
    }

    setIsSettingsLoading(true);
    post(
      '/invoice-defaults',
      {
        companyName,
        vatNumber,
        address,
        contactNumber,
        email
      },
      () => {
        if (isMounted.current) {
          dispatch(customAlert(true, 'Settings updated successfully'));
          getSettings();
        }
      },
      (err) => {
        if (isMounted.current) {
          dispatch(customAlert(true, err));
          setIsSettingsLoading(false);
        }
      }
    );
  };

  // Updated useEffect to fetch data only once
  useEffect(() => {
    isMounted.current = true;

    if (user?.id && !dataFetched) {
      setDataFetched(true);
      getSettings();
      getInsuranceDocuments();
    }

    return () => {
      isMounted.current = false;
    };
  }, [user, dataFetched, getSettings, getInsuranceDocuments]);

  if (user?.userType !== 'owner') {
    return (
      <div className={classes.unauthorizedWrapper}>
        <div className={classes.unauthorizedContent}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M8.5 9C8.5 7.067 10.067 5.5 12 5.5C13.933 5.5 15.5 7.067 15.5 9C15.5 10.6997 14.2962 12.1233 12.6975 12.4593C12.3163 12.5378 12 12.8542 12 13.25V14.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12 18.25C12.4142 18.25 12.75 17.9142 12.75 17.5C12.75 17.0858 12.4142 16.75 12 16.75C11.5858 16.75 11.25 17.0858 11.25 17.5C11.25 17.9142 11.5858 18.25 12 18.25Z" fill="currentColor" stroke="currentColor" strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <h2>Access Restricted</h2>
          <p>Only owners can access company settings</p>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.settingsWrapper}>
      <div className={classes.header}>
        <h1>Company</h1>
        <p>Manage your company details, documents and pricing</p>
      </div>

      <div className={classes.tabsWrapper}>
        <div className={classes.tabs}>
          <button
            className={`${classes.tabButton} ${activeTab === 'company' ? classes.active : ''}`}
            onClick={() => setActiveTab('company')}
          >
            Company Details
          </button>
          <button
            className={`${classes.tabButton} ${activeTab === 'insurance' ? classes.active : ''}`}
            onClick={() => setActiveTab('insurance')}
          >
            Insurance
          </button>
          <button
            className={`${classes.tabButton} ${activeTab === 'subprices' ? classes.active : ''}`}
            onClick={() => setActiveTab('subprices')}
          >
            Price List
          </button>
        </div>

        {activeTab === 'company' && (
          <div className={classes.contentContainer}>
            {isSettingsLoading ? (
              <div className={classes.loaderContainer}>
                <Loader size='large' />
              </div>
            ) : (
              <div className={classes.formGrid}>
                <div className={classes.formField}>
                  <Input
                    elementType='input'
                    elementConfig={{
                      type: 'text',
                      placeholder: 'Enter company name'
                    }}
                    value={companyName}
                    change={(e) => setCompanyName(e.target.value)}
                    id='company-name-input'
                    label='Company Name'
                    inputStyle='main'
                  />
                </div>

                <div className={classes.formField}>
                  <Input
                    elementType='input'
                    elementConfig={{
                      type: 'text',
                      placeholder: 'Enter VAT number'
                    }}
                    value={vatNumber}
                    change={(e) => setVatNumber(e.target.value)}
                    id='vat-number-input'
                    label='VAT Number'
                    inputStyle='main'
                  />
                </div>

                <div className={classes.formField}>
                  <Input
                    elementType='input'
                    elementConfig={{
                      type: 'tel',
                      placeholder: 'Enter contact number'
                    }}
                    value={contactNumber}
                    change={(e) => setContactNumber(e.target.value)}
                    id='contact-number-input'
                    label='Contact Number'
                    inputStyle='main'
                  />
                </div>

                <div className={classes.formField}>
                  <Input
                    elementType='input'
                    elementConfig={{
                      type: 'email',
                      placeholder: 'Enter email address'
                    }}
                    value={email}
                    change={(e) => setEmail(e.target.value)}
                    id='email-input'
                    label='Email'
                    inputStyle='main'
                  />
                </div>

                <div className={`${classes.formField} ${classes.fullWidth}`}>
                  <Input
                    elementType='textarea'
                    elementConfig={{
                      placeholder: 'Enter company address'
                    }}
                    value={address}
                    change={(e) => setAddress(e.target.value)}
                    id='address-input'
                    label='Company Address'
                    inputStyle='main'
                  />
                </div>

                <div className={classes.formActions}>
                  <Button
                    buttonStyle='main'
                    type='button'
                    text='Save Changes'
                    click={saveSettings}
                  />

                  {settings?.updatedAt && (
                    <div className={classes.lastUpdated}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Last updated: {new Date(settings.updatedAt).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'insurance' && (
          <div className={classes.insuranceSection}>
            {isDocumentsLoading ? (
              <div className={classes.loaderContainer}>
                <Loader size='large' />
              </div>
            ) : (
              <>
                <div className={classes.uploadCard}>
                  <div className={classes.uploadHeader}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 17V11L7 13M9 11L11 13M15 7V13L17 11M15 13L13 11M12 3H8C6.34315 3 5 4.34315 5 6V18C5 19.6569 6.34315 21 8 21H16C17.6569 21 19 19.6569 19 18V10L12 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <h3>Goods In Transport Document</h3>
                  </div>

                  <p>Upload your Goods In Transport insurance document for compliance purposes.</p>

                  <div className={classes.uploadArea}>
                    <label htmlFor="goods-in-transport-doc" className={classes.fileInputLabel}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 15V3M12 3L8 7M12 3L16 7M22 12V18C22 19.1046 21.1046 20 20 20H4C2.89543 20 2 19.1046 2 18V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>{selectedFile ? selectedFile.name : 'Choose File'}</span>
                      <input
                        type="file"
                        id="goods-in-transport-doc"
                        onChange={handleFileChange}
                        className={classes.fileInput}
                      />
                    </label>

                    <Button
                      buttonStyle='main'
                      type='button'
                      text={isFileUploading ? 'Uploading...' : 'Upload Document'}
                      click={uploadGoodsInTransportDocument}
                      disabled={!selectedFile || isFileUploading}
                    />
                  </div>
                </div>

                {insuranceDocuments.length > 0 && (
                  <div className={classes.documentsCard}>
                    <div className={classes.documentsList}>
                      <h3>Uploaded Documents</h3>
                      {insuranceDocuments.map(doc => (
                        <div key={doc.id} className={classes.documentItem}>
                          <div className={classes.documentInfo}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M14 3V7C14 7.55228 14.4477 8 15 8H19M14 3H8C6.89543 3 6 3.89543 6 5V19C6 20.1046 6.89543 21 8 21H16C17.1046 21 18 20.1046 18 19V8M14 3L19 8M10 13H14M10 17H14M10 9H10.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <div>
                              <h4>{doc.documentName}</h4>
                              <span>Updated: {new Date(doc.updatedAt).toLocaleDateString()}</span>
                            </div>
                          </div>

                          <a href={doc.signedUrl} target="_blank" rel="noopener noreferrer" className={classes.viewButton}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M2 12C2 12 5 5 12 5C19 5 22 12 22 12C22 12 19 19 12 19C5 19 2 12 2 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            View
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'subprices' && (
          <div className={classes.tabContent}>
            <div className={classes.subPricesContent}>
              <div className={classes.card}>
                <div className={classes['card-header']}>Price List</div>
                <div className={classes['price-list-body']}>
                  <PriceList clientName="Sub Contractors" clientId="subcontractors" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
